import { Box, Button, Collapse, IconButton, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Container } from '../../../../types';

const MAX_ROWS = 4;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '24px',
      boxShadow: '0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
      marginBottom: theme.spacing(3),
    },
    count: {
      color: theme.palette.error.main,
    },
    countClear: {
      color: theme.palette.info.main,
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
    },
    header: {
      cursor: 'pointer',
      // Cover the card's padding so the whole collapsed card is clickable
      margin: theme.spacing(-3),
      padding: theme.spacing(3),
    },
    chevron: {
      transition: 'transform 0.2s',
    },
    chevronExpanded: {
      transform: 'rotate(180deg)',
    },
  })
);

// Reuse requests awaiting shipping-line approval. Collapsed to a one-line
// summary until the admin wants details; actioning happens on the dedicated
// pending-requests page.
const PendingRequestsCard = ({ pendingContainers }: { pendingContainers: Container[] }) => {
  const classes = useStyles();
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  // Oldest first: the longest-waiting requests are the most urgent.
  const sorted = [...pendingContainers].sort(
    (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
  );

  return (
    <Box className={classes.container}>
      <Box
        className={classes.header}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="h6" fontWeight={600}>
          Pending Requests
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h4" fontWeight={800} className={sorted.length ? classes.count : classes.countClear}>
            {sorted.length}
          </Typography>
          <IconButton size="small">
            <ExpandMoreIcon className={`${classes.chevron} ${expanded ? classes.chevronExpanded : ''}`} />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        {sorted.length === 0 ? (
          <Typography variant="body2" color="textSecondary" mt={1}>
            No reuse requests waiting on approval
          </Typography>
        ) : (
          <Box mt={1}>
            {sorted.slice(0, MAX_ROWS).map((container) => (
              <Box key={container._id} className={classes.row}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {container.containerCode || container.referenceId}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {container.myStack ? 'My Stack' : 'Reserve'} ·{' '}
                    {container.shippingLineBookingRef || 'No booking ref'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {format(new Date(container.updated_at), 'd MMM')}
                </Typography>
              </Box>
            ))}
            {sorted.length > MAX_ROWS && (
              <Typography variant="caption" color="textSecondary" mt={1} display="block">
                +{sorted.length - MAX_ROWS} more
              </Typography>
            )}
          </Box>
        )}
        <Box mt={2}>
          <Button
            fullWidth
            variant={sorted.length ? 'contained' : 'outlined'}
            color="primary"
            endIcon={<ChevronRightIcon />}
            onClick={() => history.push('/super-admin/pending-requests')}
          >
            Review Requests
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PendingRequestsCard;
